import { useCallback, useEffect, useState } from "react";
import {
  listMyAdminTrails,
  listMyClaims,
  type AdminAssignment,
  type MyClaim,
  type User,
} from "../api";
import { usePullToRefresh } from "../usePullToRefresh";

interface Props {
  user: User;
  onNavigate: (path: string) => void;
}

export function Dashboard({ user, onNavigate }: Props) {
  const [trails, setTrails] = useState<AdminAssignment[] | null>(null);
  const [claims, setClaims] = useState<MyClaim[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([listMyAdminTrails(), listMyClaims()]);
      setTrails(t.items);
      setClaims(c.items);
    } catch {
      // Network blip — leave existing state in place
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const { pullDistance, refreshing } = usePullToRefresh(refresh);

  const pendingClaims = (claims ?? []).filter((c) => c.status === "pending");

  return (
    <>
      <PullIndicator pullDistance={pullDistance} refreshing={refreshing} />
      <h2>Welcome, {user.email.split("@")[0]}</h2>
      <p className="muted">
        You are signed in as a <strong>{user.role}</strong>.
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Trails you administer</h3>
        {trails === null && <div className="muted">Loading…</div>}
        {trails !== null && trails.length === 0 && (
          <div className="empty">
            You don't administer any trails yet.
            {pendingClaims.length === 0 && (
              <p style={{ marginTop: 12 }}>
                <button onClick={() => onNavigate("/admin")}>
                  Find or propose a trail
                </button>
              </p>
            )}
          </div>
        )}
        {trails !== null && trails.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Trail</th>
                  <th>Current status</th>
                  <th className="hide-sm">Source</th>
                </tr>
              </thead>
              <tbody>
                {trails.map((t) => (
                  <tr key={t.slug}>
                    <td><strong>{t.name}</strong></td>
                    <td>
                      <span className={`pill ${t.status.value ?? "null"}`}>
                        {t.status.value ?? "unknown"}
                      </span>
                    </td>
                    <td className="hide-sm">
                      {t.status.source && (
                        <span className="pill source">{t.status.source}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {trails !== null && trails.length > 0 && (
          <p style={{ marginTop: 12 }}>
            <button onClick={() => onNavigate("/admin")}>
              Manage status overrides →
            </button>
          </p>
        )}
      </div>

      {pendingClaims.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Pending claims</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Trail</th><th>Filed</th></tr>
              </thead>
              <tbody>
                {pendingClaims.map((c) => (
                  <tr key={c.id}>
                    <td>{c.trail.name}</td>
                    <td className="muted">{fmtDate(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted" style={{ marginTop: 12 }}>
            A curator will review your claim shortly.
          </p>
        </div>
      )}
    </>
  );
}

function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString();
}

/**
 * Visual ribbon at the top of the dashboard while the user is pulling
 * down (or while the refresh is in flight). Hidden in normal browsers
 * because the hook only emits pull distance when running standalone.
 */
function PullIndicator({
  pullDistance,
  refreshing,
}: {
  pullDistance: number;
  refreshing: boolean;
}) {
  const visible = pullDistance > 0 || refreshing;
  if (!visible) return null;
  const armed = pullDistance >= 70 || refreshing;
  return (
    <div
      className="pull-indicator"
      style={{ height: refreshing ? 44 : Math.min(pullDistance, 90) }}
      aria-live="polite"
    >
      <div className={`pull-indicator-inner ${armed ? "armed" : ""}`}>
        {refreshing ? "Refreshing…" : armed ? "Release to refresh" : "Pull to refresh"}
      </div>
    </div>
  );
}
